import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormControlLabel,
  Checkbox,
  Tab,
  Tabs,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";
import UploadImage from "../UploadImage";
import { KeyboardDatePicker } from "@material-ui/pickers";
import dayjs from "dayjs";
import { modal } from "../../../util";

const schema = yup.object().shape({
  hkiffNumber: yup.number(),
  localizations: yup.object().shape({
    en: yup.object().shape({
      name: yup.string(),
      filmCount: yup.string(),
      name: yup.string(),
      yearFilmFestival: yup.string(),
      period: yup.string(),
      bookingFloder: yup.string(),
      bookingFloderIssuuId: yup.string(),
      content: yup.string(),
      description: yup.string(),
      summary: yup.string(),
      screeningVenue: yup.string(),
    }),
    tc: yup.object().shape({
      name: yup.string(),
      filmCount: yup.string(),
      name: yup.string(),
      yearFilmFestival: yup.string(),
      period: yup.string(),
      bookingFloder: yup.string(),
      bookingFloderIssuuId: yup.string(),
      content: yup.string(),
      description: yup.string(),
      summary: yup.string(),
      screeningVenue: yup.string(),
    }),
  }),
});

const defaultFormValues = {
  image: "",
  attendance: "",
  filmNumber: "",
  countryNumber: "",
  premieresNumber: "",
  startDate: null,
  endDate: null,
  programTrailer: "",
  hkiffNumber: "",
  sequence: 0,
  createTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
  updateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
  status: {
      status: true,
      deleted: false,
  },
  localizations: {
      en: {
          filmCount: "",
          name: "",
          yearFilmFestival: "",
          period: "",
          bookingFloder: "",
          bookingFloderIssuuId: "",
          content: "",
          description: "",
          summary: "",
          screeningVenue: "",
      },
      tc: {
          filmCount: "",
          name: "",
          yearFilmFestival: "",
          period: "",
          bookingFloder: "",
          bookingFloderIssuuId: "",
          content: "",
          description: "",
          summary: "",
          screeningVenue: "",
      }
  }
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    background: "#ffffff",
  },
  input: {
    width: "100%",
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

export default function HkiffDetailPage({ disabled, ...props }) {
  const { match, history } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();
  const [activeTab, setActiveTab] = React.useState(0);

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    if (id !== "new") updateHkiff(value);
    else createHkiff(value);
  };

  const getHkiff = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getHkiffById(id);
      console.log(result);
      if(result.data.localizations.en.bookingFolderRedirectUrl == null) result.data.localizations.en.bookingFolderRedirectUrl = "";
      if(result.data.localizations.en.content == null) result.data.localizations.en.content = "";
      if(result.data.localizations.en.description == null) result.data.localizations.en.description = "";
      if(result.data.localizations.tc.bookingFolderRedirectUrl == null) result.data.localizations.tc.bookingFolderRedirectUrl = "";
      if(result.data.localizations.tc.content == null) result.data.localizations.tc.content = "";
      if(result.data.localizations.tc.description == null) result.data.localizations.tc.description = "";
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, setLoading]);

  const updateHkiff = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateHkiffById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createHkiff = async (value) => {
    try {
      const result = await apiHelper.hkiff.createHkiff(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("The post is created.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (id !== "new") getHkiff();
  }, [getHkiff, id]);

  const deleteHkiff = async () => {
    const decision = await modal.fire({
      title: "Delete HKIFF list",
      text: "Are you sure to delete this HKIFF list?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteHkiffById(id);
        console.log(result);
        history.push("/hkiff/hkiff");
      } catch (err) {
        console.log(err);
      }
    }
  };

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          HKIFF List &gt; {id === "new" ? "Add New HKIFF List" : `Edit ${defaultValues.localizations.en.name}`}
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{
          style: {
            top: "0",
            background: "#306F9D",
          },
        }}
      >
        <Tab label="English" {...a11yProps(0)} />
        <Tab label="繁體中文" {...a11yProps(1)} />
      </Tabs>

      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">

          <TabPanel value={activeTab} index={0}>
            <Box className={classes.innerBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="hkiffNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="HKIFF No."
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  direction="row"
                  spacing={3}
                  style={{ marginTop: "32px" }}
                >
                  <Grid item xs={8}>
                    {defaultValues && (
                      <UploadImage
                        name="localizations.en.bookingFloder"
                        data={defaultValues}
                        control={control}
                        Controller={Controller}
                        setValue={setValue}
                        getValues={getValues}
                        disabled={disabled}
                        uploadLabel="Booking Folder"
                        changeLabel="Booking Folder"
                        uploadFolder="HKIFF"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="localizations.en.bookingFloderIssuuId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Booking Folder Redirect URL"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {defaultValues.localizations && (
                  <>



                    <Grid item xs={12}>
                      <Box
                        fontWeight={900}
                        fontSize={13}
                        mb={2}
                        width="100%"
                      >
                        <Typography variant="p">Summary</Typography>
                      </Box>
                      <Controller
                        name="localizations.en.summary"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CKEditor
                            editor={Editor}
                            config={editorConfiguration}
                            data={field.value}
                            disabled={disabled}
                            onReady={(editor) => {
                              // You can store the "editor" and use when it is needed.
                              console.log("Editor is ready to use!", editor);
                              editor.setData(
                                defaultValues.localizations.en.summary
                              );
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              field.onChange(data);
                              // console.log({ event, editor, data });
                            }}
                            onBlur={(event, editor) => {
                              console.log("Blur.", editor);
                            }}
                            onFocus={(event, editor) => {
                              console.log("Focus.", editor);
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="localizations.en.filmCount"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Film"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="localizations.en.screeningVenue"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Screening Venue"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Controller
                        name="localizations.en.period"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Period"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Controller
                        name="localizations.en.yearFilmFestival"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Entry Year"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}


              </Grid>

            </Box>
          </TabPanel>



          <TabPanel value={activeTab} index={1}>
            <Box className={classes.innerBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="hkiffNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="HKIFF No."
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  direction="row"
                  spacing={3}
                  style={{ marginTop: "32px" }}
                >
                  <Grid item xs={8}>
                    {defaultValues && (
                      <UploadImage
                        name="localizations.tc.bookingFloder"
                        data={defaultValues}
                        control={control}
                        Controller={Controller}
                        setValue={setValue}
                        getValues={getValues}
                        disabled={disabled}
                        uploadLabel="Booking Folder"
                        changeLabel="Booking Folder"
                        uploadFolder="HKIFF"
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="localizations.tc.bookingFloderIssuuId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Booking Folder Redirect URL"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {defaultValues.localizations && (
                  <>



                    <Grid item xs={12}>
                      <Box
                        fontWeight={900}
                        fontSize={13}
                        mb={2}
                        width="100%"
                      >
                        <Typography variant="p">Summary</Typography>
                      </Box>
                      <Controller
                        name="localizations.tc.summary"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CKEditor
                            editor={Editor}
                            config={editorConfiguration}
                            data={field.value}
                            disabled={disabled}
                            onReady={(editor) => {
                              // You can store the "editor" and use when it is needed.
                              console.log("Editor is ready to use!", editor);
                              editor.setData(
                                defaultValues.localizations.tc.summary
                              );
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              field.onChange(data);
                              // console.log({ event, editor, data });
                            }}
                            onBlur={(event, editor) => {
                              console.log("Blur.", editor);
                            }}
                            onFocus={(event, editor) => {
                              console.log("Focus.", editor);
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="localizations.tc.filmCount"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Film"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="localizations.tc.screeningVenue"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Screening Venue"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Controller
                        name="localizations.tc.period"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Period"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Controller
                        name="localizations.tc.yearFilmFestival"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Entry Year"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            fullWidth
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}


              </Grid>

            </Box>
          </TabPanel>



          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="row" spacing={3}>


              <Grid item xs={8}>
                <Box
                  fontWeight={900}
                  fontSize={13}
                  mb={2}
                  width="100%"
                >
                  <Typography variant="p">Event Settings</Typography>
                </Box>
                {defaultValues && (
                  <UploadImage
                    name="image"
                    data={defaultValues}
                    control={control}
                    Controller={Controller}
                    setValue={setValue}
                    getValues={getValues}
                    disabled={disabled}
                    uploadLabel="Festival Banner"
                    changeLabel="Festival Banner"
                    uploadFolder="HKIFF"
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="programTrailer"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Program Trailer"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="attendance"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Attendance"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="filmNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Film Number"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="countryNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Country Number"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="premieresNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Premieres Number"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={3}>
                <Box
                  fontWeight={900}
                  fontSize={13}
                  mb={2}
                  width="100%"
                >
                  <Typography variant="p">Start Date</Typography>
                </Box>
                <Controller
                  name="startDate"
                  control={control}
                  render={({
                    field,
                    fieldState: { error }
                  }) => (
                    <KeyboardDatePicker
                      margin="normal"
                      id="date-picker-dialog"
                      label=""
                      className={classes.textInput}
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                      format="MM/dd/yyyy"
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={3}>
                <Box
                  fontWeight={900}
                  fontSize={13}
                  mb={2}
                  width="100%"
                >
                  <Typography variant="p">End Date</Typography>
                </Box>
                <Controller
                  name="endDate"
                  control={control}
                  render={({
                    field,
                    fieldState: { error }
                  }) => (
                    <KeyboardDatePicker
                      margin="normal"
                      id="date-picker-dialog"
                      label=""
                      className={classes.textInput}
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                      format="MM/dd/yyyy"
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                    />
                  )}
                />
              </Grid>




              <Grid
                container
                item
                xs={12}
                spacing={3}
                style={{ marginTop: "52px", marginLeft: 0 }}
              >
                <Grid item xs={3} style={{ paddingLeft: "4px" }}>
                  <Controller
                    control={control}
                    name="status.status"
                    render={({
                      field: { value, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <ToggleButtonGroup
                        {...rest}
                        value={value}
                        onChange={(event, newFormats) => {
                          onChange(newFormats);
                        }}
                        exclusive
                        aria-label="Status"
                        className={classes.input}
                      >
                        <ToggleButton
                          value={true}
                          aria-label="Active"
                          style={{ width: "50%" }}
                          disabled={disabled}
                        >
                          Active
                    </ToggleButton>
                        <ToggleButton
                          value={false}
                          aria-label="Inactive"
                          style={{ width: "50%" }}
                          disabled={disabled}
                        >
                          Inactive
                    </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Grid>
              </Grid>

            </Grid>
          </Box>


          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Publish
              </Button>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#962013",
                  padding: "9px 16px",
                }}
                onClick={() => deleteHkiff()}
              >
                Delete
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
